/**
 * Files' own side of combined search: `FilesSource.search` (`../files/source.ts`)
 * already narrows to files this person can see (mine, or shared with them)
 * whose name matches -- this only turns that into rows.
 *
 * `FilesSource` carries no "is this mine" flag of its own, so this also asks
 * `mine()` (already in memory the same instant `Files.tsx` itself loads it,
 * and cheap: the fake and any real adapter both answer it without a search
 * of their own) to know which matched files are the signed-in person's own,
 * for the same two things `mine` decides on the section's own list: whether
 * the owner is worth naming in the snippet, and which tab a row's route
 * opens on so the file is actually there to find.
 */

import { formatBytes } from '@wtfalch/utils/format';
import type { AgoraFile, FilesSource } from '../files/source.ts';
import type { AgoraRoute } from '../routes.ts';
import type { SearchRow } from './types.ts';

function routeFor(agoraId: string, mine: boolean): AgoraRoute {
  return { kind: 'agora', agoraId, section: 'files', location: { tab: mine ? 'mine' : 'shared' } };
}

function rowFor(agoraId: string, file: AgoraFile, mine: boolean): SearchRow {
  return {
    id: file.id,
    title: file.name,
    snippet: mine ? formatBytes(file.bytes) : `${formatBytes(file.bytes)} · ${file.owner.name}`,
    time: file.updatedAt ?? undefined,
    route: routeFor(agoraId, mine),
  };
}

export async function searchFiles(
  source: FilesSource,
  agoraId: string,
  query: string,
  signal?: AbortSignal,
): Promise<readonly SearchRow[]> {
  // `mine()` only decides which tab a row opens on; if it fails, the row
  // still shows, treated as shared, rather than losing every match.
  const [matches, mine] = await Promise.all([
    source.search(query, signal),
    source.mine(signal).catch(() => []),
  ]);
  const mineIds = new Set(mine.map((f) => f.id));
  return matches.map((file) => rowFor(agoraId, file, mineIds.has(file.id)));
}
